/**
 * Auth Client
 * Безопасное управление токенами и API запросы с автоматическим refresh
 * 
 * @typedef {import('./auth/types').TokenResponse} TokenResponse
 * @typedef {import('./auth/types').AuthError} AuthError
 */

// ============ Token Storage ============

/** AccessToken хранится только в памяти (memory-only) */
let accessToken = null;

/** CSRF token хранится в sessionStorage (для защиты мутирующих запросов) */
const CSRF_TOKEN_KEY = 'auth_csrf_token';

/** Base URL для API запросов */
let baseUrl = '';

/** Flag для предотвращения множественных refresh запросов */
let isRefreshing = false;

/** Queue запросов ожидающих refresh токена */
let failedQueue = [];

// ============ Configuration ============

/**
 * Инициализация authClient с базовым URL
 * В dev режиме (Vite) можно оставить пустым - запросы пойдут через прокси
 * @param {string} [apiUrl] - Base URL для Auth API (опционально)
 */
export function initAuthClient(apiUrl) {
  // В dev режиме используем пустой baseUrl чтобы запросы шли через Vite proxy
  // В production указываем полный URL через VITE_AUTH_API_URL
  baseUrl = apiUrl ?? (import.meta.env.VITE_AUTH_API_URL ?? '');
}

// ============ Token Management ============

/**
 * Получить access token из памяти
 * @returns {string|null}
 */
export function getAccessToken() {
  return accessToken;
}

/**
 * Проверить наличие валидного access token
 * @returns {boolean}
 */
export function isAuthenticated() {
  if (!accessToken) return false;

  // Проверить expiration (базовая проверка)
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const expiresAt = payload.exp * 1000;
    return expiresAt > Date.now();
  } catch {
    return false;
  }
}

/**
 * Сохранить tokens (accessToken в памяти, refreshToken в localStorage)
 * Поддерживает оба формата: camelCase и snake_case
 * @param {TokenResponse} tokens
 */
export function setTokens(tokens) {
  console.log('[authClient] setTokens called with:', { 
    hasAccessToken: !!tokens.accessToken, 
    hasAccess_token: !!tokens.access_token,
    hasCsrfToken: !!tokens.csrfToken,
    hasCsrf_token: !!tokens.csrf_token
  });
  
  // Поддержка и camelCase (accessToken) и snake_case (access_token)
  const access = tokens.accessToken || tokens.access_token;
  const csrf = tokens.csrfToken || tokens.csrf_token;
  
  if (!access) {
    console.error('[authClient] setTokens: No access token in response', tokens);
    return;
  }
  
  accessToken = access;
  if (csrf) {
    sessionStorage.setItem(CSRF_TOKEN_KEY, csrf);
    console.log('[authClient] CSRF token saved to sessionStorage');
  } else {
    console.warn('[authClient] No CSRF token in response');
  }
  
  console.log('[authClient] Tokens saved successfully');
}

/**
 * Очистить токены (при logout)
 */
export function clearTokens() {
  accessToken = null;
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
}

/**
 * Получить CSRF token из sessionStorage
 * @returns {string|null}
 */
export function getCsrfToken() {
  return sessionStorage.getItem(CSRF_TOKEN_KEY);
}

/**
 * Проверить наличие CSRF token (для определения активной сессии)
 * @returns {boolean}
 */
export function hasCsrfToken() {
  return !!sessionStorage.getItem(CSRF_TOKEN_KEY);
}

// ============ Token Refresh ============

/**
 * Refresh access token (refresh token передаётся автоматически через httpOnly cookie)
 * @returns {Promise<string>}
 */
async function refreshAccessToken() {
  try {
    const response = await fetch(`${baseUrl}/api/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Body пустой - refresh token передаётся через httpOnly cookie
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearTokens();
        throw new Error('Refresh token expired or invalid');
      }
      throw new Error(`Token refresh failed: ${response.statusText}`);
    }

    /** @type {TokenResponse} */
    const data = await response.json();
    setTokens(data);
    return data.accessToken || data.access_token;
  } catch (error) {
    clearTokens();
    throw error;
  }
}

/**
 * Добавить refresh token в queue ожидающих запросов
 * @param {(token: string) => void} resolve
 * @param {(error: Error) => void} reject
 */
function addToFailedQueue(resolve, reject) {
  failedQueue.push({ resolve, reject });
}

/**
 * Обработать очередь запросов после успешного refresh
 * @param {string|null} token
 * @param {Error|null} error
 */
function processQueue(token, error) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });

  failedQueue = [];
  isRefreshing = false;
}

// ============ Fetch Wrapper ============

/**
 * Обёртка для fetch с автоматическим Authorization header и refresh на 401
 * @template T
 * @param {string} endpoint - API endpoint (без базового URL)
 * @param {RequestInit & { params?: Record<string, string|number|boolean> }} [options] - RequestInit options + params для query string
 * @returns {Promise<T>}
 */
export async function authFetch(endpoint, options = {}) {
  const { params, ...fetchOptions } = options;

  // Построить URL с query параметрами
  let url = `${baseUrl}${endpoint}`;
  if (params) {
    const queryString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      queryString.append(key, String(value));
    });
    url += `?${queryString}`;
  }

  // Добавить Authorization header если есть access token
  const headers = new Headers(fetchOptions.headers || {});
  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Добавить X-CSRF-Token для мутирующих запросов (POST, PUT, PATCH, DELETE)
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const mutatingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
  if (mutatingMethods.includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }

  // Установить Content-Type по умолчанию
  if (!headers.has('Content-Type') && fetchOptions.body) {
    headers.set('Content-Type', 'application/json');
  }

  let response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  // Обработать 401 (token expired)
  if (response.status === 401) {
    // Если уже идёт refresh, добавить запрос в очередь
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        addToFailedQueue(
          (newToken) => {
            headers.set('Authorization', `Bearer ${newToken}`);
            resolve(
              fetch(url, { ...fetchOptions, headers, credentials: 'include' })
                .then((r) => {
                  if (!r.ok) throw new Error(`HTTP ${r.status}`);
                  return r.json();
                })
            );
          },
          reject
        );
      });
    }

    // Начать refresh
    isRefreshing = true;
    try {
      const newToken = await refreshAccessToken();
      headers.set('Authorization', `Bearer ${newToken}`);
      processQueue(newToken, null);

      // Повторить оригинальный запрос с новым токеном
      response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include',
      });
    } catch (error) {
      processQueue(null, error);
      throw error;
    }
  }

  // Обработать ошибки
  if (!response.ok) {
    /** @type {Partial<AuthError>} */
    let errorData = {
      status: response.status,
      message: response.statusText,
    };

    try {
      errorData = await response.json();
    } catch {
      // Если не JSON, использовать statusText
    }

    const error = new Error(errorData.message || `HTTP ${response.status}`);
    error.status = response.status;
    error.code = errorData.code;
    error.errors = errorData.errors;

    // Глобальный error-toast — только для серверных/неожиданных ошибок.
    // 401/403 на auth-эндпоинтах обрабатывает вызывающий код (login/refresh).
    if (!isSilentAuthError(endpoint, response.status)) {
      try {
        const { notify } = await import('shared/utils/notify');
        notify.error(humanizeAuthError(error));
      } catch {
        /* notify ещё не доступен — игнорируем */
      }
    }

    throw error;
  }

  return response.json();
}

/**
 * Эндпоинты/статусы, для которых не показываем глобальный toast (обработка локально).
 */
function isSilentAuthError(endpoint, status) {
  const silentEndpoints = ['/api/auth/login', '/api/auth/refresh-token', '/api/auth/me'];
  if (silentEndpoints.some((p) => endpoint.startsWith(p))) return true;
  // 401 в любом auth-вызове — обрабатывает refresh/redirect
  if (status === 401) return true;
  return false;
}

/**
 * Преобразовать ошибку API в человекочитаемое сообщение.
 */
function humanizeAuthError(error) {
  if (error.status >= 500) return 'Сервер недоступен. Попробуйте позже.';
  if (error.status === 429) return 'Слишком много запросов. Подождите и повторите.';
  if (error.status === 403) return 'Недостаточно прав для выполнения операции.';
  if (error.status === 404) return 'Ресурс не найден.';
  return error.message || 'Произошла ошибка при обращении к серверу.';
}

// ============ Helper Functions ============

/**
 * Декодировать JWT payload (без верификации подписи!)
 * Используется только для проверки expiration на клиенте
 * @param {string} token
 * @returns {Record<string, any>|null}
 */
export function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Проверить истек ли токен
 * @param {string} token
 * @param {number} [bufferSeconds=60]
 * @returns {boolean}
 */
export function isTokenExpired(token, bufferSeconds = 60) {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;

  const expiresAt = payload.exp * 1000;
  const now = Date.now();
  return expiresAt - now < bufferSeconds * 1000;
}
